<template>
  <div class="flex flex-col gap-7 px-6">
    <div class="flex flex-col gap-2">
      <BaseText
        type="label"
        size="lg"
        class="text-text-loud"
      >
        Install Pixel
      </BaseText>
      <BaseText
        type="body"
        size="md"
        class="text-text-normal"
      >
        Add this snippet before the closing &lt;/head&gt; tag on every page of your site, or add it as a custom HTML tag in Google Tag Manager. It tracks conversions so we can calculate your credits.
      </BaseText>
    </div>

    <!-- snippet -->
    <div
      v-if="!isPixelVerified"
      class="w-full bg-background-disabled rounded-2xl p-3 space-y-4"
    >
      <div class="flex items-center justify-between">
        <BaseText
          type="label"
          size="sm"
          class="bg-background text-text-muted"
        >
          &lt;/&gt; Code
        </BaseText>
        <button
          v-clipboard:copy="snippet"
          v-clipboard:success="handleCopySuccess"
          v-clipboard:error="handleCopyError"
          :disabled="loading || !snippet"
          class="base-btn bg-white flex items-center gap-1"
        >
          <StaticCopyIcon class="w-5 h-5 text-icon-normal" />
          <BaseText
            type="label"
            size="sm"
            class="text-text-muted"
          >
            Copy
          </BaseText>
        </button>
      </div>

      <!-- Mirrors the snippet's text-xs / leading-5 rows so the box doesn't jump on load -->
      <div
        v-if="loading"
        class="flex flex-col"
      >
        <div
          v-for="(width, index) in skeletonLineWidths"
          :key="`snippet-skeleton-${index}`"
          class="flex items-center h-5"
        >
          <div
            class="skeleton h-3 rounded"
            :style="{ width }"
          />
        </div>
      </div>
      <pre
        v-else
        class="overflow-x-auto font-mono text-xs leading-5 text-text-normal whitespace-pre"
      ><code>{{ snippet }}</code></pre>
    </div>

    <!-- pixel install status  -->
    <div
      v-if="pixelStatusDisplay"
      class="status-banner"
      :class="statusBanner.class"
    >
      <!-- Icons -->
      <div class="flex-shrink-0">
        <component
          :is="statusBanner.icon"
          :width="20"
          :height="20"
          :duration="1"
        />
      </div>
      <div class="space-y-1">
        <BaseText
          type="label"
          size="sm"
        >
          {{ pixelStatusDisplay.title }}
        </BaseText>
        <BaseText
          type="body"
          size="sm"
        >
          {{ pixelStatusDisplay.description }}
        </BaseText>
      </div>
    </div>

    <BaseText
      v-if="isPixelVerified && !loadingSubmit"
      type="body"
      size="sm"
      class="text-text-muted"
    >
      You're all set. We'll keep watching for events and let you know if anything stops firing.
    </BaseText>

    <div class="flex items-start">
      <button
        class="black-btn-primary flex items-center gap-1 pl-2.5 py-1.5 rounded-md"
        :class="submitting || isPixelVerified ? 'pr-1.5' : 'pr-2.5'"
        :disabled="loading || loadingSubmit || submitting"
        @click="handleNext"
      >
        <BaseText
          type="label"
          size="sm"
          class="text-white whitespace-nowrap"
        >
          {{ nextButtonText }}
        </BaseText>
        <BaseLoadingSpinnerCircle
          v-if="submitting"
          class="text-white w-5 h-5"
          :duration="1"
        />
        <ChevronIcon
          v-else-if="isPixelVerified"
          class-name="text-white transform -rotate-90"
          :width="20"
          :height="20"
        />
      </button>
    </div>
  </div>
</template>

<script>
import LensAPI from '../../../../api/lens/LensRequests'

// Icons
import ChevronIcon from '../../../globals/Icons/ChevronIcon.vue'
import StaticCopyIcon from '../../../globals/Icons/StaticCopyIcon.vue'
import InfoSimpleIcon from '../../../globals/Icons/InfoSimpleIcon.vue'

export default {
  name: 'SpendBackStepPixel',
  components: {
    ChevronIcon,
    StaticCopyIcon,
    InfoSimpleIcon
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    lens: {
      type: Object,
      default: () => ({})
    },
    nextStepLabel: {
      type: String,
      default: null
    },
    isLastStep: {
      type: Boolean,
      default: false
    },
    submitting: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      snippet: null,
      status: null,
      lastEventAt: null,
      siteUrl: null,
      loading: false,
      loadingSubmit: false,
      skeletonLineWidths: ['45%', '80%', '70%', '85%', '60%', '40%']
    }
  },
  computed: {
    pixelStatusDisplay () {
      if (this.loadingSubmit) {
        return {
          title: 'Checking your site...',
          description: 'Open your site in a new tab to trigger a pageview. This can take up to 30 seconds.'
        }
      }
      switch (this.status) {
        case 'verified': {
          return {
            title: 'Pixel verified',
            description: 'Successfully received your first event.'
          }
        }
        case 'not_installed':
          return {
            title: `Pixel not found on ${this.siteDomain || 'your site'}`,
            description: 'Make sure the snippet is pasted and published live, not just saved as a draft.'
          }
        case 'inactive':
          return {
            title: 'Pixel not found',
            description: 'Make sure the snippet is pasted and published live, not just saved as a draft.'
          }
        case 'pending':
          return {
            title: 'Waiting for the first event',
            description: "We can see the pixel on your site but it hasn't sent any data. Visit a page to trigger a pageview, then check again."
          }
        default:
          return null
      }
    },
    siteDomain () {
      if (!this.siteUrl) return null
      return this.siteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '')
    },
    isPixelVerified () {
      return this.status === 'verified'
    },
    nextButtonText () {
      if (this.loadingSubmit) return 'Checking...'
      if (this.isPixelVerified) return `Continue to ${this.nextStepLabel || 'Next'}`
      return 'Check status'
    },
    statusBanner () {
      if (this.loadingSubmit) {
        return { icon: 'BaseLoadingSpinnerCircle', class: 'loading-banner' }
      }
      switch (this.status) {
        case 'verified':
          return { icon: 'InfoSimpleIcon', class: 'active-banner' }
        case 'pending_activation':
        case 'pending':
          return { icon: 'InfoSimpleIcon', class: 'pending-banner' }
        case 'not_installed':
        case 'inactive':
          return { icon: 'InfoSimpleIcon', class: 'error-banner' }
        default:
          return { icon: 'InfoSimpleIcon', class: '' }
      }
    }
  },
  async mounted () {
    await this.fetchPixel()
  },
  methods: {
    async handleNext () {
      if (this.isPixelVerified) {
        this.$emit('continue')
        return
      }

      this.loadingSubmit = true
      try {
        await this.fetchPixel(false)
      } finally {
        this.loadingSubmit = false
      }
    },
    async fetchPixel (showLoading = true) {
      this.loading = showLoading
      try {
        const { data: response } = await LensAPI.SpendBack.getPixel(this.lensId)
        this.snippet = response?.data?.snippet || null
        this.status = response?.data?.status || null
        this.lastEventAt = response?.data?.last_event_at || null
        this.siteUrl = response?.data?.site_url || null
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch pixel snippet. Please try again.'
        })
      } finally {
        this.loading = false
      }
    },
    formatLastEvent (timestamp) {
      const date = new Date(timestamp)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    handleCopySuccess () {
      this.$showAlert({
        type: 'success',
        message: 'Pixel snippet copied to clipboard'
      })
    },
    handleCopyError () {
      this.$showAlert({
        type: 'error',
        message: 'Failed to copy the snippet. Please copy it manually.'
      })
    }
  }
}
</script>

<style scoped>
.base-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
    0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
  padding: 6px 8px 6px 8px;
}

.base-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
    0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.status-banner {
  padding: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 12px;
}

.active-banner {
  background-color: #EBFFFA;
  color: #005043;
}

.pending-banner {
  background-color: #FFF6DE;
  color: #66381A;
}

.error-banner {
  background-color: #FFEFF3;
  color: #7C001F;
}

.loading-banner {
  background-color: #EEF8FF;
  color: #002583;
}

</style>
